<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div
            class="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700/50"
        >
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                Experiência do checkout
            </h3>
        </div>

        <div v-if="loading" class="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
            Carregando...
        </div>

        <template v-else>
            <div class="space-y-2 px-5 pt-3">
                <p
                    v-if="deleted"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Este produtor está excluído: as ofertas dele não são resolvíveis e ficam no
                    Legado. Ele não recebe marcação nova; só é possível voltar a "segue a chave
                    global".
                </p>
                <p
                    v-if="killSwitchOn"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    A chave global está em Desligado. Esta marcação só passa a valer quando a
                    chave sair de Desligado.
                </p>
                <p
                    v-if="propagating"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Mudança em propagação: o comprador pode ver o valor anterior por até 60
                    segundos.
                </p>
                <p
                    v-if="explanationError"
                    class="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300"
                >
                    Consulta indisponível: não foi possível consultar a liberação no Payments
                    agora. A marcação gravada continua valendo.
                </p>
            </div>

            <dl class="divide-y divide-gray-100 dark:divide-gray-700/50">
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Marcação atual</dt>
                    <dd class="col-span-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ currentLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Chave global</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ switchLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Liberação efetiva</dt>
                    <dd class="col-span-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ experienceLabel }}
                        <span
                            v-if="explanation"
                            class="text-xs font-normal text-gray-500 dark:text-gray-400"
                        >
                            ({{ originLabel }})
                        </span>
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">
                        Retidas por domínio próprio
                    </dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ retainedLabel }}
                    </dd>
                </div>
            </dl>

            <div class="space-y-3 border-t border-gray-100 px-5 py-3 dark:border-gray-700/50">
                <single-select
                    v-model="value"
                    label="Experiência deste produtor"
                    :options="options"
                />

                <div>
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-white"
                        for="producer-experience-reason"
                    >
                        Motivo da mudança
                    </label>
                    <!-- Sem maxlength: motivo longo recebe aviso antes de salvar, nunca truncado. -->
                    <textarea
                        id="producer-experience-reason"
                        v-model="reason"
                        rows="2"
                        class="text-input-sm"
                        placeholder="Por que esta mudança está sendo feita?"
                    ></textarea>
                    <div class="mt-1 flex items-center justify-between">
                        <span v-if="error" class="text-xs text-red-600 dark:text-red-400">
                            {{ error }}
                        </span>
                        <span v-else></span>
                        <span
                            class="text-xs"
                            :class="
                                reason.length > reasonMaxLength
                                    ? 'font-medium text-red-600 dark:text-red-400'
                                    : 'text-gray-500 dark:text-gray-400'
                            "
                        >
                            {{ reason.length }}/{{ reasonMaxLength }}
                        </span>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button
                        type="button"
                        class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                        :disabled="submitting"
                        @click.prevent="submit"
                    >
                        {{ submitting ? "Salvando..." : "Salvar experiência" }}
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import SingleSelect from "../../../components/forms/SingleSelect.vue";

const SWITCH_LABELS = { off: "Desligado", selective: "Seletivo", all: "Todos" };

const EXPERIENCE_LABELS = { legacy: "Legado", v2: "Checkout 2.0" };

// Mesmos rótulos do OfferExperienceLookup.vue: a origem vem do mesmo enum do Payments.
// Na consulta por produtor, `unresolvable` só aparece para o produtor excluído.
const ORIGIN_LABELS = {
    kill_switch: "Chave global em Desligado",
    offer: "Exceção da oferta",
    producer: "Produtor",
    default: "Padrão da chave global",
    unresolvable: "Produtor excluído",
    custom_domain: "Domínio próprio",
    funnel: "Funil",
    funnel_limit: "Funil acima do limite",
    source_unavailable: "Fonte indisponível",
    v2_unconfigured: "Host do Checkout 2.0 não configurado",
};

const UNAVAILABLE = "Consulta indisponível";

const RETAINED_UNCOUNTABLE = "Não foi possível contar.";

// Mesmo limite do UpdateExperienceTargetRequest (C17).
const REASON_MAX_LENGTH = 500;

export default {
    name: "ProducerExperienceSection",

    components: { SingleSelect },

    props: ["user"],

    data() {
        return {
            loading: true,
            submitting: false,
            value: "follow",
            savedValue: "follow",
            reason: "",
            error: "",
            reasonMaxLength: REASON_MAX_LENGTH,
            propagating: false,
            deleted: false,
            explanation: null,
            explanationError: null,
            options: [
                { value: "follow", label: "segue a chave global" },
                { value: "legacy", label: "Legado" },
                { value: "v2", label: "Checkout 2.0" },
            ],
        };
    },

    computed: {
        currentLabel() {
            return this.options.find((option) => option.value === this.savedValue)?.label || "—";
        },

        killSwitchOn() {
            return (
                this.explanation?.origin === "kill_switch" || this.explanation?.switch === "off"
            );
        },

        // US-012 AC-5 e EC-8: quantas ofertas do produtor ficam no Legado por domínio próprio.
        retainedLabel() {
            if (!this.explanation) {
                return UNAVAILABLE;
            }

            // As ofertas do produtor excluído ficam no Legado antes do domínio próprio.
            if (this.deleted) {
                return "—";
            }

            const retained = this.explanation.retained_by_domain;

            if (retained === null || retained === undefined) {
                return RETAINED_UNCOUNTABLE;
            }

            const count =
                retained === 1
                    ? "1 oferta retida por domínio próprio"
                    : retained + " ofertas retidas por domínio próprio";
            const partial = this.explanation.retained_partial ? " (contagem parcial)" : "";

            return count + partial + ". Elas só vão para o Checkout 2.0 no card 6.4.";
        },

        switchLabel() {
            const value = this.explanation?.switch;

            return value ? SWITCH_LABELS[value] : UNAVAILABLE;
        },

        experienceLabel() {
            const value = this.explanation?.experience;

            return value ? EXPERIENCE_LABELS[value] : UNAVAILABLE;
        },

        originLabel() {
            const value = this.explanation?.origin;

            return value ? ORIGIN_LABELS[value] || value : UNAVAILABLE;
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;

            axios
                .get("/api/checkout-experience/producers/" + this.user.id)
                .then(({ data }) => {
                    this.savedValue = data.value === null ? "follow" : data.value;
                    this.value = this.savedValue;
                    this.propagating = data.propagating;
                    this.deleted = data.producer?.deleted === true;
                    this.explanation = data.explanation;
                    this.explanationError = data.explanation_error;
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: this.errorText(
                                response,
                                "Falha ao carregar a experiência do checkout.",
                            ),
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        submit() {
            if (this.submitting) {
                return;
            }

            const reason = this.reason.trim();

            if (reason === "") {
                this.error = "O motivo da mudança é obrigatório.";
                return;
            }

            if (reason.length > REASON_MAX_LENGTH) {
                this.error =
                    "O motivo da mudança deve ter no máximo " +
                    REASON_MAX_LENGTH +
                    " caracteres. Agora tem " +
                    reason.length +
                    ".";
                return;
            }

            this.error = "";
            this.submitting = true;

            axios
                .put("/api/checkout-experience/producers/" + this.user.id, {
                    value: this.value,
                    reason,
                })
                .then(({ data }) => {
                    this.reason = "";

                    this.$notify(
                        {
                            group: data.changed ? "success" : "error",
                            title: data.changed ? "Experiência atualizada" : "Nada mudou",
                            text: data.changed
                                ? "A nova marcação vale para o comprador em até 60 segundos."
                                : "O produtor já estava nesse valor. Nada foi registrado.",
                        },
                        4000,
                    );

                    this.fetch();
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: this.errorText(response, "Falha ao salvar a experiência."),
                        },
                        6000,
                    );
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        errorText(response, fallback) {
            const data = response?.data;

            if (Array.isArray(data?.errors) && data.errors.length) {
                return data.errors.join(" ");
            }

            return data?.message || fallback;
        },
    },
};
</script>
